/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  type AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { BUILD_TARGET, HOST_URL } from "config";
import {
  getCoachToken,
  hasCoachSession,
  isLoggedIn,
  isStoredJwtSession,
  loginPathFor,
  refreshCoachToken,
  setCoachToken,
} from "./coachAuth";
import { appLog, notifyAppLogout } from "utils/webview";
import { captureException } from "utils/sentry";

export type CustomResponseFormat<T = any> = {
  code: number;
  debugMessage?: string;
  message?: string;
  status?: number;
  error?: string;
  data?: T;
  result?: T;
};

interface CustomInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<CustomResponseFormat>>;
  };

  getUri(config?: AxiosRequestConfig): string;

  request<T>(config: AxiosRequestConfig): Promise<T>;

  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;

  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;

  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  // 코치 JWT 세션이면 Bearer 로 보내고 레거시 헤더는 떼어 낸다 (서버는 X-Auth-Token 이 있으면 그걸 먼저 본다).
  const coachToken = getCoachToken();
  if (coachToken) {
    config.headers.Authorization = `Bearer ${coachToken}`;
    delete config.headers["X-Auth-Token"];
  }
  if (BUILD_TARGET === "dev") {
    console.info(
      "[",
      config.method?.toUpperCase(),
      config.url,
      "]",
      "\nparams: ",
      config.params
    );
  }
  return config;
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  if (BUILD_TARGET === "dev") {
    // console.info(
    //   `[${response.config.method?.toUpperCase()} ${
    //     response.config.url
    //   }]\ndata: ${JSON.stringify(response.data)}`
    // );
    // console.log(response.data);
  }
  return response.data;
};

const axiosInstance: CustomInstance = axios.create({
  withCredentials: true,
  baseURL: HOST_URL,
  timeout: 15000, // 15초 타임아웃
  headers: {
    // "X-Api-Key": API_KEY,
    // "X-Auth-Token": "",
    // "X-App-Id": "w:123",
  },
});

const onRejected = async (error: AxiosError<CustomResponseFormat>) => {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retryCount?: number;
    _coachRetry?: boolean;
  };

  // 코치 JWT 세션: 액세스 토큰(5분) 만료 401 → 리프레시 쿠키로 재발급 후 원 요청 1회 재시도.
  // 재발급까지 실패하면(쿠키 만료·탈퇴) 세션을 비우고 코치 로그인으로 보낸다.
  // 로그인·토큰 발급 경로의 401 은 "세션 만료"가 아니라 "인증 실패"다 — 갱신·재시도·세션 정리 어느 것도
  // 하지 않고 호출부로 돌려준다. (JWT 세션이 남은 브라우저에서 /pc/login 폴링이 401 을 받아도 튕기지 않게)
  const authEndpoint = isAuthEndpoint(originalRequest?.url);

  const isUnauthorized =
    error.response?.status === 401 || error.response?.data?.status === 401;
  // 메모리 토큰이 아직 없어도(복원 전) 프로필이 JWT 세션이면 이 경로다 — 레거시 핸들러가 세션을 지우면 안 된다.
  if (
    isUnauthorized &&
    !authEndpoint &&
    (hasCoachSession() || isStoredJwtSession()) &&
    originalRequest &&
    !originalRequest._coachRetry &&
    !originalRequest.url?.includes("/user/v2/token/refresh")
  ) {
    originalRequest._coachRetry = true;
    const newToken = await refreshCoachToken();
    if (newToken) {
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    }
    const loginPath = loginPathFor(null); // 프로필 역할 우선: 코치 /coach, (내부자) 유저 역할이면 /login
    clearAuthHeaders();
    localStorage.removeItem("user");
    window.location.href = `${window.location.origin}${loginPath}`;
    return Promise.reject(error);
  }

  if (error.config) console.log(error);

  // 네트워크 에러 또는 타임아웃 처리
  if (!error.response) {
    if (error.code === "ECONNABORTED") {
      console.error("Request timeout:", error.message);
      captureException(error, {
        tags: { action: "axios_timeout" },
      });
    } else if (error.message === "Network Error") {
      console.error("Network error:", error.message);

      // 첫 Network Error는 warning으로 보고
      captureException(error, {
        tags: { action: "axios_network_error" },
        level: "warning",
      });

      // Network Error 시 자동 재시도 (최대 2회)
      const retryCount = originalRequest._retryCount || 0;
      if (retryCount < 2) {
        console.log(`Retrying request (attempt ${retryCount + 1}/2)...`);
        originalRequest._retryCount = retryCount + 1;

        // 재시도 전 짧은 대기
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * (retryCount + 1))
        );

        try {
          return await axiosInstance(originalRequest);
        } catch (retryError) {
          // 재시도도 실패한 경우
          if (originalRequest._retryCount >= 2) {
            console.error("All retry attempts failed. Reloading page...");
            appLog("Network error: reloading page");

            // Sentry에 재시도 실패 보고
            captureException(retryError, {
              tags: { action: "axios_network_error_retry_failed" },
              extra: { retryCount: originalRequest._retryCount },
            });

            // 페이지 새로고침
            window.location.reload();
          }
          throw retryError;
        }
      }
    }
  }

  if (error.response?.data) {
    const statusCode = error.response?.data.status;

    if (authEndpoint) {
      return Promise.reject(error);
    }

    if (statusCode === 401 || statusCode === 403) {
      console.log(`${statusCode} ${JSON.stringify(error.config)}`);
      appLog(`${statusCode} ${JSON.stringify(error.config)}`);
      // 코치는 코치 로그인으로 보낸다 — 프로필을 지우기 전에 역할을 읽어 둔다.
      const loginPath = loginPathFor(null);
      clearAuthHeaders();
      localStorage.removeItem("user");
      await notifyAppLogout();
      window.location.href = `${window.location.origin}${loginPath}`;
    }
  }

  return Promise.reject(error);
};

/** 로그인·토큰 발급 계열 요청인지. 이 경로들의 401 은 세션 만료가 아니라 인증 실패다. */
function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  const path = url.split("?")[0];
  return (
    path.endsWith("/user/login") ||
    path.endsWith("/user/v2/login") ||
    path.includes("/user/v2/token/refresh") ||
    path.includes("/auth/onetime-token/")
  );
}

axiosInstance.interceptors.request.use(onRequest);
// axiosInstance.interceptors.response.use(onResponse);
axiosInstance.interceptors.response.use(onResponse, onRejected);

axiosInstance.defaults.headers["X-Auth-Token"] = localStorage.getItem("user")
  ? JSON.parse(localStorage.getItem("user") || "").accessToken
  : undefined;

export const setAuthHeaders = (accessToken: string, appId?: string) => {
  if (
    axiosInstance.defaults?.headers &&
    axiosInstance.defaults.headers.common
  ) {
    // console.log("current", accessToken, appId);
    // @ts-ignore
    axiosInstance.defaults.headers["X-Auth-Token"] = accessToken;
    if (appId) {
      // @ts-ignore
      axiosInstance.defaults.headers["X-App-Id"] = appId;
    }
  }
};

export const clearAuthHeaders = () => {
  setCoachToken(null);
  if (axiosInstance.defaults?.headers) {
    // @ts-ignore
    axiosInstance.defaults.headers["X-Auth-Token"] = undefined;
    // @ts-ignore
    axiosInstance.defaults.headers["X-App-Id"] = undefined;
  }
};

export const setAppIdHeaders = (appId: string) => {
  if (
    axiosInstance.defaults?.headers &&
    axiosInstance.defaults.headers.common
  ) {
    axiosInstance.defaults.headers["X-App-Id"] = appId;
  }
};

export const setLocale = (locale: string) => {
  if (
    axiosInstance.defaults?.headers &&
    axiosInstance.defaults.headers.common
  ) {
    // console.log("current", accessToken, appId);
    // @ts-ignore
    axiosInstance.defaults.headers["X-Lang-Code"] = locale;
  }
};

export { axiosInstance };

/** 요청에 인증을 실을 수 있는 상태인지 — 레거시 헤더가 잡혀 있거나 코치 JWT 토큰이 메모리에 있다. */
export const hasAuthHeader = (): boolean =>
  !!axiosInstance.defaults.headers["X-Auth-Token"] || hasCoachSession();

/**
 * react-query `enabled` 용. 기존 `!!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken` 을
 * 대체한다 — 코치는 토큰을 userAtom 에 두지 않으므로 그 식이 항상 false 가 된다.
 */
export const isAuthReady = (
  user?: { accessToken?: string; authMode?: string } | null
): boolean => hasAuthHeader() && isLoggedIn(user);
